import { Link, useParams } from "@tanstack/react-router";
import { AdminShell } from "@project/admin/components/admin-shell";
import { SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import { ArrowLeft, CheckCircle2, Clock, XCircle, Phone } from "lucide-react";
import {
  mockAdmissionApplications,
  type AdmissionStatus,
} from "@project/lib/mock-admissions";

function statusBadge(s: AdmissionStatus) {
  const map: Record<AdmissionStatus, string> = {
    New: "bg-sky-500/15 text-sky-700",
    "Under Review": "bg-amber-500/15 text-amber-700",
    Approved: "bg-emerald-500/15 text-emerald-700",
    Rejected: "bg-rose-500/15 text-rose-700",
  };
  return <Badge className={map[s]}>{s}</Badge>;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border/60 flex flex-col gap-0.5 border-b py-2 last:border-0">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground">
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

export default function AdmissionApplicationDetail() {
  const { id } = useParams({
    from: "/admin/_admissions/admission-applications/$id",
  });
  const app =
    mockAdmissionApplications.find(a => a.appNo === id) ??
    mockAdmissionApplications[0];

  return (
    <AdminShell
      title={`Application ${app.appNo}`}
      subtitle={`${app.elderName} · submitted ${app.date}`}
      actions={
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/admission-applications">
            <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back
          </Link>
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex-1">
          <div className="text-xs text-muted-foreground">Current Status</div>
          <div className="mt-1">{statusBadge(app.status)}</div>
        </div>
        <Button size="sm" variant="outline">
          <Clock className="mr-1 h-3.5 w-3.5" /> Mark Under Review
        </Button>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-600/90">
          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approve
        </Button>
        <Button size="sm" variant="destructive">
          <XCircle className="mr-1 h-3.5 w-3.5" /> Reject
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard
          title="Applicant"
          subtitle="Person who submitted the application"
        >
          <div className="space-y-1">
            <Row label="Name" value={app.applicantName} />
            <Row label="Mobile" value={app.applicantMobile} />
            <Row label="Address" value={app.applicantAddress} />
          </div>
        </SectionCard>

        <SectionCard
          title="Elder Information"
          subtitle="Person being recommended for admission"
        >
          <div className="space-y-1">
            <Row label="Name" value={app.elderName} />
            <Row label="Father's Name" value={app.fatherName} />
            <Row label="Age" value={String(app.age)} />
            <Row label="Village" value={app.village} />
            <Row label="Taluka" value={app.taluka} />
            <Row label="District" value={app.district} />
          </div>
        </SectionCard>

        <SectionCard title="Situation & Condition" className="lg:col-span-2">
          <div className="grid gap-1 md:grid-cols-3">
            <Row label="Current Living" value={app.currentLiving} />
            <Row label="Physical Condition" value={app.physicalCondition} />
            <Row label="Reason for Admission" value={app.reason} />
          </div>
        </SectionCard>

        <SectionCard title="Contact Persons">
          <div className="space-y-2">
            {app.contacts.map(c => (
              <div
                key={`${c.name}-${c.relation}-${c.mobile}`}
                className="bg-muted/30 flex items-center justify-between rounded-lg border border-border p-3"
              >
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {c.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {c.relation}
                  </div>
                </div>
                <a
                  href={`tel:${c.mobile.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Phone className="h-3.5 w-3.5" /> {c.mobile}
                </a>
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Income & Remarks">
          <div className="space-y-1">
            <Row label="Pension" value={app.pension} />
            <Row label="Other Income" value={app.otherIncome} />
            <Row label="Remarks" value={app.remarks} />
          </div>
        </SectionCard>
      </div>
    </AdminShell>
  );
}
